import {PaginatedResponse} from 'src/services/types';
import {PageInfo} from 'src/views/college-portal/primary-market-states/types';

export const INITIAL_PAGE_INFO: PageInfo = {pageIndex: 0, pageSize: 6};

export function generateBlankPage<DataType>(
  rows?: DataType[],
  opts: Partial<PageInfo> = {}
): PaginatedResponse<DataType> {
  rows = rows ? rows : [];
  return {
    rows,
    limit: rows.length,
    offset: 0,
    totalRows: rows.length,
    pageCount: 1,
    pageInfo: {
      pageIndex: opts.pageIndex || INITIAL_PAGE_INFO.pageIndex,
      pageSize: opts.pageSize || rows.length,
    },
  };
}

export interface RawPaginatedResponse {
  limit: number;
  offset: number;
  totalRows: number;
  rows: [];
  metadata?: object;
}

export function formatPaginatedResponse<DataType>(
  body: RawPaginatedResponse,
  pageInfo: PageInfo,
  formatFn: (row) => DataType
): PaginatedResponse<DataType> {
  return {
    pageInfo,
    limit: body.limit,
    offset: body.offset,
    totalRows: body.totalRows,
    rows: body.rows.map(formatFn),
    pageCount: Math.ceil(body.totalRows / body.limit),
  };
}

interface PaginationStats {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  totalPages: number;
  startPage: number;
  endPage: number;
  startIndex: number;
  endIndex: number;
  pages: number[];
}

// See https://jasonwatmore.com/post/2018/08/07/javascript-pure-pagination-logic-in-vanilla-js-typescript
export function paginate(
  totalItems: number,
  currentPage = 1,
  pageSize = 10,
  maxPages = 10
): PaginationStats {
  // calculate total pages
  const totalPages = Math.ceil(totalItems / pageSize);

  // ensure current page isn't out of range
  if (currentPage < 1) {
    currentPage = 1;
  } else if (currentPage > totalPages) {
    currentPage = totalPages;
  }

  let startPage: number, endPage: number;
  if (totalPages <= maxPages) {
    // total pages less than max so show all pages
    startPage = 1;
    endPage = totalPages;
  } else {
    // total pages more than max so calculate start and end pages
    const maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
    if (currentPage <= maxPagesBeforeCurrentPage) {
      // current page near the start
      startPage = 1;
      endPage = maxPages;
    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
      // current page near the end
      startPage = totalPages - maxPages + 1;
      endPage = totalPages;
    } else {
      // current page somewhere in the middle
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  // calculate start and end item indexes
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

  // create an array of pages to ng-repeat in the pager control
  const pages = Array.from(Array(endPage + 1 - startPage).keys()).map((i) => startPage + i);

  // return object with all pager properties required by the view
  return {
    totalItems: totalItems,
    currentPage: currentPage,
    pageSize: pageSize,
    totalPages: totalPages,
    startPage: startPage,
    endPage: endPage,
    startIndex: startIndex,
    endIndex: endIndex,
    pages: pages,
  };
}
